import React, { useState } from 'react';
import { voiceAgents } from '../../data/adminMock';
import { Search, Plus, MoreVertical, Play, Pause, Trash2, Settings, BarChart2 } from 'lucide-react';

const AdminAgents = () => {
  const [agents, setAgents] = useState(voiceAgents);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionMenu, setActionMenu] = useState(null);

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.owner.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleStatusChange = (agentId, newStatus) => {
    setAgents(agents.map(agent =>
      agent.id === agentId ? { ...agent, status: newStatus } : agent
    ));
    setActionMenu(null);
  };

  const handleDelete = (agentId) => {
    if (window.confirm('Are you sure you want to delete this agent?')) {
      setAgents(agents.filter(agent => agent.id !== agentId));
    }
    setActionMenu(null);
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Voice Agents</h1>
          <p className="text-gray-400">Manage all voice AI agents across the platform</p>
        </div>
        <button className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors">
          <Plus className="w-5 h-5" />
          Create Agent
        </button>
      </div>

      {/* Search & Filters */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search agents by name or owner..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-[#1a1a1f] border border-gray-700 rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-gray-500 outline-none focus:border-teal-400"
            />
          </div>
          <select className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400">
            <option>All Languages</option>
            <option>English</option>
            <option>Spanish</option>
            <option>French</option>
            <option>German</option>
          </select>
          <select className="bg-[#1a1a1f] border border-gray-700 rounded-lg px-4 py-2.5 text-gray-300 outline-none focus:border-teal-400">
            <option>All Status</option>
            <option>Active</option>
            <option>Paused</option>
            <option>Inactive</option>
          </select>
        </div>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredAgents.map((agent) => (
          <div
            key={agent.id}
            className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-teal-400/30 transition-all"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-teal-400/10 rounded-xl flex items-center justify-center">
                <span className="text-teal-400 font-bold text-lg">{agent.name.charAt(0)}</span>
              </div>
              <div className="relative">
                <button
                  onClick={() => setActionMenu(actionMenu === agent.id ? null : agent.id)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <MoreVertical className="w-5 h-5 text-gray-400" />
                </button>
                {actionMenu === agent.id && (
                  <div className="absolute right-0 top-full mt-1 w-44 bg-[#1a1a1f] border border-gray-700 rounded-lg shadow-xl z-10">
                    <button className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors">
                      <Settings className="w-4 h-4" />
                      Configure
                    </button>
                    <button className="w-full flex items-center gap-2 px-4 py-2.5 text-gray-300 hover:bg-gray-800 transition-colors">
                      <BarChart2 className="w-4 h-4" />
                      Analytics
                    </button>
                    {agent.status === 'active' ? (
                      <button
                        onClick={() => handleStatusChange(agent.id, 'paused')}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-yellow-400 hover:bg-gray-800 transition-colors"
                      >
                        <Pause className="w-4 h-4" />
                        Pause Agent
                      </button>
                    ) : (
                      <button
                        onClick={() => handleStatusChange(agent.id, 'active')}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-green-400 hover:bg-gray-800 transition-colors"
                      >
                        <Play className="w-4 h-4" />
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(agent.id)}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-red-400 hover:bg-gray-800 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </div>

            <h3 className="text-white font-semibold text-lg mb-1">{agent.name}</h3>
            <p className="text-gray-500 text-sm mb-4">by {agent.owner}</p>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Language</span>
                <span className="text-white text-sm">{agent.language}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Total Calls</span>
                <span className="text-white text-sm font-medium">{agent.calls.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 text-sm">Created</span>
                <span className="text-white text-sm">{agent.created}</span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-gray-800">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                agent.status === 'active' ? 'bg-green-400/10 text-green-400' :
                agent.status === 'paused' ? 'bg-yellow-400/10 text-yellow-400' :
                'bg-gray-400/10 text-gray-400'
              }`}>
                {agent.status.charAt(0).toUpperCase() + agent.status.slice(1)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminAgents;
